// Per-browser "have I read this?" state for the research library. Keys are
// scoped per authenticated user (their OAuth sub) so a shared browser profile
// never shows one account's read state to another, mirroring assistant-store.
//
// Two pieces of state:
//   - `baseline`: when this user first opened the library on this browser.
//     Items published at or before it are pre-existing backlog, never "new" —
//     so a first visit does not light up the whole library.
//   - `seen`: items the user has actually opened, newest last, each carrying
//     the timestamp it landed so pruning can advance the baseline instead of
//     letting an aged-out item resurface as new.
//
// Everything here is best-effort: a storage failure degrades to "nothing is
// new", never to a broken page.

const SEEN_MAX = 500;

function storageKey(scope: string): string {
  return `almanac.research.v1.${scope}.seen`;
}

/** One opened item: its id plus when it landed (`published_at ?? created_at`). */
export interface ResearchSeenEntry {
  id: string;
  landed: string;
}

export interface ResearchSeenState {
  /**
   * ISO timestamp of this user's first visit on this browser, advanced when
   * pruning drops an entry so the dropped item never counts as new again.
   */
  baseline: string;
  /** Items already opened, in open order (newest last). */
  seen: ResearchSeenEntry[];
}

/**
 * Postgres `to_char(..., 'OF')` renders a whole-hour zone as a bare `+00`,
 * which `Date.parse` rejects outright (NaN) rather than tolerating. Pad it to
 * `+00:00` before parsing so timestamps straight off the API compare correctly.
 */
const BARE_HOUR_OFFSET = /T\d{2}:\d{2}:\d{2}[+-]\d{2}$/u;

/** Parse an API timestamp to epoch ms, or NaN when it is not a usable date. */
export function parseResearchTimestamp(value: string): number {
  if (BARE_HOUR_OFFSET.test(value)) return Date.parse(`${value}:00`);
  return Date.parse(value);
}

function isEntry(value: unknown): value is ResearchSeenEntry {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return typeof candidate.id === 'string' && typeof candidate.landed === 'string';
}

function isState(value: unknown): value is ResearchSeenState {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  if (typeof candidate.baseline !== 'string') return false;
  return Array.isArray(candidate.seen) && candidate.seen.every(isEntry);
}

function read(scope: string): ResearchSeenState | null {
  let raw: string | null;
  try {
    raw = localStorage.getItem(storageKey(scope));
  } catch {
    return null;
  }
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isState(parsed)) return null;
  return parsed;
}

function write(scope: string, state: ResearchSeenState): void {
  try {
    localStorage.setItem(storageKey(scope), JSON.stringify(state));
  } catch {
    // Read state is best-effort — never let a quota failure break the page.
  }
}

/**
 * Read this user's state, stamping a baseline on first use so the very first
 * visit establishes "everything already here is not new". `now` is injectable
 * for tests.
 */
export function loadResearchSeen(
  scope: string,
  now: string = new Date().toISOString(),
): ResearchSeenState {
  const existing = read(scope);
  if (existing) return existing;
  const fresh: ResearchSeenState = { baseline: now, seen: [] };
  write(scope, fresh);
  return fresh;
}

export interface ResearchNewnessInput {
  research_id: string;
  published_at: string | null;
  created_at: string;
}

/**
 * Record that the user opened an item. No-ops when the viewer identity has not
 * resolved yet (`scope === null`), so callers can fire it unconditionally.
 * Returns the resulting state so a caller can update React state without a
 * second read.
 *
 * The list is capped. When it overflows, the entries that landed longest ago
 * are dropped and the baseline advances past them, so a pruned item stays
 * "read" rather than resurfacing as New. The only cost is that the oldest
 * still-unread items age out of the badge too — the right thing to lose first.
 */
export function markResearchSeen(
  scope: string | null,
  item: ResearchNewnessInput,
  now: string = new Date().toISOString(),
): ResearchSeenState | null {
  if (!scope) return null;
  const state = loadResearchSeen(scope, now);
  const seen = state.seen.filter((entry) => entry.id !== item.research_id);
  seen.push({ id: item.research_id, landed: item.published_at ?? item.created_at });
  if (seen.length <= SEEN_MAX) {
    const next: ResearchSeenState = { baseline: state.baseline, seen };
    write(scope, next);
    return next;
  }

  // Unparseable timestamps sort oldest so they are shed first; they can never
  // read as new anyway, and they must not drag the baseline anywhere.
  const landedMs = (entry: ResearchSeenEntry): number => {
    const parsed = parseResearchTimestamp(entry.landed);
    if (Number.isNaN(parsed)) return -Infinity;
    return parsed;
  };
  const byLanded = [...seen].sort((a, b) => landedMs(a) - landedMs(b));
  const dropped = byLanded.slice(0, seen.length - SEEN_MAX);
  const keptIds = new Set(byLanded.slice(seen.length - SEEN_MAX).map((entry) => entry.id));

  let baseline = state.baseline;
  let baselineMs = parseResearchTimestamp(baseline);
  for (const entry of dropped) {
    const candidate = landedMs(entry);
    if (candidate === -Infinity) continue;
    if (Number.isNaN(baselineMs) || candidate > baselineMs) {
      baseline = entry.landed;
      baselineMs = candidate;
    }
  }

  const next: ResearchSeenState = {
    baseline,
    seen: seen.filter((entry) => keptIds.has(entry.id)),
  };
  write(scope, next);
  return next;
}

/**
 * True when the viewer has not opened this item AND it landed after their
 * baseline visit. An unparseable timestamp is treated as not-new: a badge is a
 * nudge, and a wrong one is worse than a missing one.
 */
export function isResearchNew(state: ResearchSeenState, item: ResearchNewnessInput): boolean {
  if (state.seen.some((entry) => entry.id === item.research_id)) return false;
  const baseline = parseResearchTimestamp(state.baseline);
  const landed = parseResearchTimestamp(item.published_at ?? item.created_at);
  if (Number.isNaN(baseline) || Number.isNaN(landed)) return false;
  return landed > baseline;
}
